import { PageTemplateName } from '@efdigital/ll-addon-content';
import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import {
  type Cohort,
  DataTypeContent,
  DataTypeSystem,
  generateRandomSlug,
  type Page,
  type PageWidgetCalendar,
  type PageWidgetMarkdown,
  PageWidgetType,
  type UserGroup,
} from '@efdigital/ll-addon-data';
import {
  dummyUserAdmin,
  dummyUserParticipant,
} from '@efdigital/ll-admin-v2/utils/user-groups';
import type { Store } from '@warp-drive/core';
import { Type } from '@warp-drive/core/types/symbols';

import { setAsyncBelongsTo, setAsyncHasMany } from '../async-setter.ts';
import { deepSave } from '../deep-save.ts';
import { createCohortSurvey } from './quiz-generator.ts';

export async function generateCohort(
  cohort: Cohort,
  context: { store: Store; picklists: PicklistsService }
) {
  await createUserGroups(cohort, context.store);
  await createWelcomePage(cohort, context.store);

  const survey = await createCohortSurvey(cohort, context);
  setAsyncHasMany(cohort, 'certificates', [survey]);
}

/**
 * Creates user groups for a cohort if they don't exist
 * @param cohort - The cohort to create user groups for
 * @param store - The store to create the user groups in
 */
async function createUserGroups(cohort: Cohort, store: Store) {
  if (!cohort.group?.id) {
    const group = store.createRecord<UserGroup>(DataTypeSystem.UserGroup, {
      title: `${cohort.slug}-participants`,
      userIds: [dummyUserParticipant],
    });
    await deepSave(group, store, { maxDepth: 2 });
    setAsyncBelongsTo(cohort, 'group', group);
  }

  if (!cohort.admingroup?.id) {
    const admingroup = store.createRecord<UserGroup>(DataTypeSystem.UserGroup, {
      title: `${cohort.slug}-admins`,
      userIds: [dummyUserAdmin],
    });
    await deepSave(admingroup, store, { maxDepth: 2 });
    setAsyncBelongsTo(cohort, 'admingroup', admingroup);
  }
}

async function createWelcomePage(cohort: Cohort, store: Store) {
  if (cohort.pages?.length > 0) {
    return;
  }

  // Create a welcome page for the new cohort
  const program = await cohort.program;
  const client = await cohort.client;

  const programName = program?.title || 'Program Name';
  const clientName = client?.title || '==Partner / Institution Name==';

  const welcomeWidget: PageWidgetMarkdown = {
    [Type]: DataTypeContent.PageWidgetMarkdown,
    title: 'Welcome',
    css: '',
    wid: generateRandomSlug(),
    widgetType: PageWidgetType.Markdown,
    markdown: `We are delighted to welcome you to ${programName}, delivered in partnership with ${clientName}.

This platform will host all key information, resources, and activities related to your program. Please ensure you check back regularly for updates and materials throughout your learning journey.

### Program Overview

==Leadership / Capability Area== is a key element of your ongoing development as a ==Role / Audience Description==. This customised program has been collaboratively designed by ==Internal Team / Organisation== and ==External Partner==.

The program aims to enable:

* A shared language and understanding of ==Leadership / Capability Area==
* Networking and collaboration across ==Teams / Functions / Regions==
* Skill development focused on ==Core Capability Areas – e.g. Self, People, Business==
* Self-reflection and professional growth through assessments, feedback, and guided reflection`,
  };

  const calendarWidget: PageWidgetCalendar = {
    [Type]: DataTypeContent.PageWidgetCalendar,
    title: 'Add important dates here',
    css: '',
    wid: generateRandomSlug(),
    widgetType: PageWidgetType.Calendar,
    calendarItemIds: [],
    listDisplay: 'list',
  };

  const importantNoteWidget: PageWidgetMarkdown = {
    [Type]: DataTypeContent.PageWidgetMarkdown,
    title: 'Important Note',
    css: '',
    wid: generateRandomSlug(),
    widgetType: PageWidgetType.Markdown,
    markdown: `### Important Note

To gain the maximum benefit from this program, it is essential that you engage fully in all components. To help create a productive, respectful, and engaging learning environment, we ask all participants to commit to the following:

* Be prepared to actively contribute
* Be willing to take risks, experiment, and learn from experience
* Be open to challenge and constructive feedback
* Encourage inclusive participation and help create a climate of trust and psychological safety`,
  };

  const welcomePage = store.createRecord<Page>(DataTypeContent.Page, {
    title: 'Welcome',
    slug: generateRandomSlug(),
    template: PageTemplateName.Widgets,
    markdown: '',
    category1: 'Xceed',
    category2: 'Cohort',
    category3: program?.id || '',
    category4: cohort.id || '',
    verb: 'http://adlnet.gov/expapi/verbs/experienced',
    activityType: 'http://adlnet.gov/expapi/activities/page',
    client,
    widgets: [welcomeWidget, calendarWidget, importantNoteWidget],
    systemLineage: 'll-admin-v2',
  });

  // Add welcome page to the beginning of the cohort's pages
  const pages = await cohort.pages;
  pages.unshift(welcomePage);
}
